import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Settings, Trash2, ArrowRight, ListChecks, ArrowLeft } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { AddBoardModal } from '../components/AddBoardModal';
import { BoardSettingsModal } from '../components/BoardSettingsModal';
import { KanbanBoard } from '../components/KanbanBoard';
import BookingInfoModal from '../components/BookingInfoModal';
import { getDashboardData } from '../lib/boards';
import { TasksBoard, UserTypeInfo, BoardWithColumns } from '../types/board';
import { fixBoardsTableRLS } from '../lib/boardsMigrationFix';
import { Booking } from '../lib/bookings';

export default function Tasks() {
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const [userType, setUserType] = useState<'admin' | 'resource' | null>(null);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [resourceName, setResourceName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedBoard, setSelectedBoard] = useState<TasksBoard | null>(null);
  const [openedBoard, setOpenedBoard] = useState<TasksBoard | null>(null);
  const [boardData, setBoardData] = useState<BoardWithColumns | null>(null);
  const [boards, setBoards] = useState<TasksBoard[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Booking info modal state
  const [bookingInfoModalOpen, setBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // New state for mobile navigation pattern
  const [showMobileMenu, setShowMobileMenu] = useState(true);

  // Set initial sidebar state based on screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const isMobileScreen = window.innerWidth < 1024;
      setIsMobile(isMobileScreen);
      if (isMobileScreen) {
        setSidebarOpen(false);
        // Reset to menu view when switching to mobile
        setShowMobileMenu(true);
      } else {
        // On desktop, always show sidebar
        setSidebarOpen(true);
        setShowMobileMenu(false);
      }
    };

    // Check on mount
    checkScreenSize();

    // Add resize listener
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Load dashboard data when component mounts
  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user || !selectedBusiness || !supabase) {
        setIsLoading(false);
        setBoardsLoading(false);
        return;
      }

      // Show UI immediately, start loading data in background
      setIsLoading(false);

      try {
        // Fix RLS policy issue on first load
        await fixBoardsTableRLS();
        
        // Get all dashboard data in one API call
        const dashboardData = await getDashboardData(selectedBusiness.id, user.id);
        
        // Set user type and info
        setUserType(dashboardData.userType);
        setResourceId(dashboardData.resourceId || null);
        setResourceName(dashboardData.resourceName || null);
        
        // Set boards data
        setBoards(dashboardData.boards);
        
        // Auto-open default board (data will load lazily when clicked)
        if (dashboardData.defaultBoard) {
          setOpenedBoard(dashboardData.defaultBoard);
          // Load default board data lazily
          const { getBoardWithColumns } = await import('../lib/kanban');
          const fullBoardData = await getBoardWithColumns(dashboardData.defaultBoard.id);
          setBoardData(fullBoardData);
          console.log('Automatically opened default board:', dashboardData.defaultBoard.board_title);
        }
        
        console.log('Dashboard data loaded successfully:', {
          userType: dashboardData.userType,
          boardsCount: dashboardData.boards.length,
          hasDefaultBoard: !!dashboardData.defaultBoard,
          hasBoardData: !!dashboardData.boardData
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setBoards([]);
        setUserType('admin');
        setResourceId(null);
        setResourceName(null);
      } finally {
        setBoardsLoading(false);
      }
    };

    loadDashboardData();
  }, [user, selectedBusiness]);

  // Handle board creation
  const handleBoardCreated = async () => {
    if (!user || !selectedBusiness) return;
    
    setBoardsLoading(true);
    try {
      // Reload dashboard data after board creation
      const dashboardData = await getDashboardData(selectedBusiness.id, user.id);
      setBoards(dashboardData.boards);
      
      // Auto-open default board if available (data will load lazily)
      if (dashboardData.defaultBoard) {
        setOpenedBoard(dashboardData.defaultBoard);
        setBoardData(null); // Will be loaded lazily
      }
    } catch (error) {
      console.error('Error refreshing boards:', error);
    } finally {
      setBoardsLoading(false);
    }
  };

  // Handle mobile navigation
  const handleMobileBoardSelect = async (board: TasksBoard) => {
    setOpenedBoard(board);
    setBoardData(null); // Clear previous board data
    
    if (isMobile) {
      setShowMobileMenu(false);
    }

    // Load board data lazily
    try {
      const { getBoardWithColumns } = await import('../lib/kanban');
      const fullBoardData = await getBoardWithColumns(board.id);
      setBoardData(fullBoardData);
    } catch (error) {
      console.error('Error loading board data:', error);
    }
  };

  const handleMobileBackToMenu = () => {
    if (isMobile) {
      setShowMobileMenu(true);
      setOpenedBoard(null);
      setBoardData(null);
    }
  };

  // Handle board click - open the board and load its data
  const handleBoardClick = async (board: TasksBoard) => {
    if (isMobile) {
      handleMobileBoardSelect(board);
    } else {
      setOpenedBoard(board);
      setBoardData(null); // Clear previous board data

      // Load board data lazily
      try {
        const { getBoardWithColumns } = await import('../lib/kanban');
        const fullBoardData = await getBoardWithColumns(board.id);
        setBoardData(fullBoardData);
      } catch (error) {
        console.error('Error loading board data:', error);
      }
    }
  };

  // Handle board settings
  const handleOpenBoardSettings = (board: TasksBoard, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent board click event
    setSelectedBoard(board);
    setIsSettingsModalOpen(true);
  };

  const handleCloseBoardSettings = () => {
    setIsSettingsModalOpen(false);
    setSelectedBoard(null);
  };

  // Handle closing the opened board
  const handleCloseBoardView = () => {
    setOpenedBoard(null);
    setBoardData(null);
  };

  const handleOpenBookingInfo = (booking: Booking) => {
    setSelectedBooking(booking);
    setBookingInfoModalOpen(true);
  };

  return (
    <div className="flex bg-gray-100 relative overflow-hidden" data-tasks-page style={{ 
      height: 'calc(100vh - 6rem)', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      {/* Mobile Menu View - Full Screen */}
      {isMobile && showMobileMenu && (
        <div className="fixed top-16 left-0 right-0 bg-gray-50 z-50 flex flex-col" style={{ height: 'calc(100vh - 8rem - 16px)' }}>
          {/* Mobile Menu Header */}
          <div className="p-6 border-b border-gray-200/60 bg-white shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg">
                  <ListChecks className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Tasks</h2>
                  <p className="text-sm text-gray-600">My Boards</p>
                </div>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                disabled={!userType || !selectedBusiness}
                className="flex items-center px-4 py-2 bg-purple-700 text-white rounded-xl hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-sm"
              >
                <Plus size={16} className="mr-2" />
                Add Board
              </button>
            </div>
          </div>

          {/* Mobile Menu Items - Boards List */}
          <div className="flex-1 overflow-y-auto p-4">
            {boardsLoading ? (
              <nav className="space-y-3 pb-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="p-4 rounded-xl bg-white shadow-sm border border-gray-100 animate-pulse">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </nav>
            ) : boards.length === 0 ? (
              <div className="text-center py-16">
                <div className="mx-auto h-24 w-24 text-gray-400 mb-6">
                  <ListChecks size={96} className="mx-auto" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">No task boards yet</h3>
                <p className="text-gray-600 mb-6">
                  Get started by creating your first task board.
                </p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="inline-flex items-center space-x-2 bg-purple-700 text-white px-6 py-3 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-lg font-semibold"
                >
                  <Plus size={20} />
                  <span>Create Your First Board</span>
                </button>
              </div>
            ) : (
              <nav className="space-y-3 pb-4">
                {boards.map((board) => (
                  <button
                    key={board.id}
                    onClick={() => handleMobileBoardSelect(board)}
                    className="w-full text-left p-4 rounded-xl transition-all duration-200 group bg-white hover:bg-purple-50 shadow-sm hover:shadow-md border border-gray-100"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {board.board_title.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="text-base font-medium text-gray-900 group-hover:text-purple-900">
                          {board.board_title}
                        </div>
                        <div className="text-sm text-gray-500 group-hover:text-purple-700 mt-1">
                          {board.assigned_users?.length || 0} member{(board.assigned_users?.length || 0) !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-purple-600" />
                    </div>
                  </button>
                ))}
              </nav>
            )}
          </div>
        </div>
      )}

      {/* Desktop/Mobile Content View */}
      {(!isMobile || !showMobileMenu) && (
        <>
          {/* Left Sidebar - Desktop Only */}
                     {!isMobile && (
             <div className={`${
               sidebarOpen ? 'w-80' : 'w-12'
             } flex-shrink-0 transition-all duration-300 ease-in-out bg-gray-50 border-r border-gray-200/60 flex flex-col overflow-hidden relative shadow-xl rounded-r-2xl`}>
               
               {/* Toggle Button - Top Right Corner */}
               <button
                 onClick={() => setSidebarOpen(!sidebarOpen)}
                 className="absolute top-4 right-4 p-2 bg-white rounded-xl hover:bg-gray-50 shadow-sm active:scale-95 z-20 transition-all duration-200"
                 title={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
               >
                 {sidebarOpen ? (
                   <ChevronLeft size={18} className="text-gray-700" />
                 ) : (
                   <ChevronRight size={18} className="text-gray-700" />
                 )}
               </button>

               {/* Header */}
               <div className="p-6 border-b border-gray-200/60 flex items-center justify-between">
                 <h1 className={`${
                   sidebarOpen ? 'text-2xl' : 'text-xs'
                 } font-bold text-gray-900 transition-all duration-300 ${
                   sidebarOpen ? 'opacity-100' : 'opacity-0'
                 }`}>
                   {sidebarOpen ? 'Tasks' : 'T'}
                 </h1>
               </div>
               
               {/* My Boards Section - Expanded */}
               <div className={`flex-1 p-6 transition-all duration-300 flex flex-col ${
                 sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
               }`}>
                 <div className="flex items-center justify-between mb-6 flex-shrink-0">
                   <h2 className="text-base font-semibold text-gray-900">My Boards</h2>
                   <button
                     onClick={() => setIsModalOpen(true)}
                     disabled={!userType || !selectedBusiness}
                     className="flex items-center text-sm px-3 py-2 bg-purple-700 text-white rounded-xl hover:bg-purple-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 active:scale-95 shadow-sm"
                   >
                     <Plus size={14} className="mr-1.5" />
                     Add Board
                   </button>
                 </div>
                 
                 {/* Boards List */}
                 <div className="space-y-3 flex-1 overflow-y-auto">
                   {boardsLoading ? (
                     <div className="space-y-3">
                       {[...Array(3)].map((_, i) => (
                         <div key={i} className="flex items-center p-4 rounded-xl bg-white shadow-sm animate-pulse">
                           <div className="w-8 h-8 bg-gray-200 rounded-lg mr-4"></div>
                           <div className="flex-1 h-4 bg-gray-200 rounded"></div>
                         </div>
                       ))}
                     </div>
                   ) : boards.length === 0 ? (
                     <div className="text-base text-gray-600 bg-white p-6 rounded-xl shadow-sm border border-gray-200/50">
                       No boards yet. Create your first board!
                     </div>
                   ) : (
                     boards.map((board) => (
                       <div
                         key={board.id}
                         className={`flex items-center p-4 rounded-xl cursor-pointer group transition-all duration-200 shadow-sm active:scale-95 ${
                           openedBoard?.id === board.id 
                             ? 'bg-purple-50 border border-purple-200/50 shadow-md' 
                             : 'bg-white hover:bg-gray-50 border border-gray-200/50 hover:shadow-md'
                         }`}
                         onClick={() => handleBoardClick(board)}
                       >
                         <div className="flex-1 min-w-0">
                           <div className={`text-base font-semibold truncate ${
                             openedBoard?.id === board.id 
                               ? 'text-purple-900' 
                               : 'text-gray-900'
                           }`}>
                             {board.board_title}
                           </div>
                           <div className="text-sm text-gray-600 mt-1">
                             {board.assigned_users.length} user{board.assigned_users.length !== 1 ? 's' : ''}
                           </div>
                         </div>
                         <button
                           onClick={(e) => handleOpenBoardSettings(board, e)}
                           className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-all duration-200 active:scale-95"
                           title="Board Settings"
                         >
                           <Settings size={16} className="text-gray-600" />
                         </button>
                       </div>
                     ))
                   )}
                 </div>
               </div>

               {/* Collapsed State - Desktop Mini Boards List */}
               <div className={`flex-1 p-3 transition-all duration-300 ${
                 !sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
               }`}>
                 <div className="space-y-3 mt-6">
                   {boards.slice(0, 3).map((board) => (
                     <div
                       key={board.id}
                       className={`w-10 h-10 rounded-xl cursor-pointer border transition-all duration-200 shadow-sm active:scale-95 ${
                         openedBoard?.id === board.id 
                           ? 'bg-purple-600 border-purple-700' 
                           : 'bg-white border-gray-200 hover:bg-gray-50 hover:shadow-md'
                       }`}
                       onClick={() => handleBoardClick(board)}
                       title={board.board_title}
                     >
                       <div className="w-full h-full flex items-center justify-center">
                         <span className={`text-sm font-bold ${
                           openedBoard?.id === board.id ? 'text-white' : 'text-gray-700'
                         }`}>
                           {board.board_title.charAt(0).toUpperCase()}
                         </span>
                       </div>
                     </div>
                   ))}
                   {boards.length > 3 && (
                     <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                       <span className="text-xs font-bold text-gray-600">+{boards.length - 3}</span>
                     </div>
                   )}
                 </div>
               </div>
             </div>
           )}

           {/* Main Content Area */}
           <div className="flex-1 flex flex-col overflow-hidden">
             {/* Mobile Content Header with Back Button */}
             {isMobile && !showMobileMenu && (
               <div className="bg-white border-b border-gray-200 p-4 flex items-center">
                 <button
                   onClick={handleMobileBackToMenu}
                   className="p-2 rounded-lg hover:bg-gray-100 mr-3 transition-colors"
                 >
                   <ArrowLeft className="h-5 w-5 text-gray-600" />
                 </button>
                 <div className="flex items-center space-x-3">
                   <div className="p-2 rounded-lg bg-purple-100">
                     <ListChecks className="h-5 w-5 text-purple-700" />
                   </div>
                   <div>
                     <h1 className="text-lg font-semibold text-gray-900">
                       {openedBoard ? openedBoard.board_title : 'Tasks'}
                     </h1>
                     <p className="text-sm text-gray-600">
                       {openedBoard ? `${openedBoard.assigned_users?.length || 0} member${(openedBoard.assigned_users?.length || 0) !== 1 ? 's' : ''}` : 'Task Management'}
                     </p>
                   </div>
                 </div>
               </div>
             )}

                           {/* Main Content */}
              <div className="flex-1 overflow-hidden">
                {openedBoard ? (
                  /* Show Kanban Board when a board is selected */
                  <div className="h-full overflow-hidden">
                    {boardData ? (
                      <KanbanBoard 
                        board={openedBoard} 
                        boardData={boardData}
                        onClose={handleCloseBoardView}
                        onOpenBookingInfo={handleOpenBookingInfo}
                      />
                    ) : (
                      /* Skeleton loader for board content */
                      <div className="h-full p-6 overflow-y-auto">
                        <div className="flex items-center justify-between mb-6">
                          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
                          <div className="h-8 w-24 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                        <div className="flex gap-6 h-full overflow-x-auto">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="flex-shrink-0 w-80">
                              <div className="h-6 w-32 bg-gray-200 rounded mb-4 animate-pulse"></div>
                              <div className="space-y-3">
                                {[...Array(4)].map((_, j) => (
                                  <div key={j} className="h-24 bg-gray-100 rounded-lg animate-pulse"></div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Show Dashboard when no board is selected */
                  <div className="h-full p-8 bg-gray-100 flex flex-col">
                    <div className="max-w-6xl mx-auto flex flex-col h-full">
                      <div className="bg-gray-50 rounded-2xl p-8 shadow-sm border border-gray-200/50 mb-8 flex-shrink-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <h1 className="text-3xl font-bold text-gray-900">Task Management</h1>
                            <p className="text-lg text-gray-600 mt-2">
                              {userType === 'admin' 
                                ? 'Manage your team\'s tasks and workflows' 
                                : `Welcome back, ${resourceName || 'Team Member'}`
                              }
                            </p>
                          </div>
                          <button
                            onClick={() => setIsModalOpen(true)}
                            className="flex items-center space-x-3 bg-purple-700 text-white px-6 py-3 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-md text-lg font-semibold"
                          >
                            <Plus size={22} />
                            <span>New Board</span>
                          </button>
                        </div>
                      </div>

                      {isLoading ? (
                        /* Loading state */
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                          {[...Array(6)].map((_, i) => (
                            <div key={i} className="bg-gray-50 rounded-2xl border border-gray-200/50 p-8 shadow-sm">
                              <div className="animate-pulse">
                                <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-3"></div>
                                <div className="h-4 bg-gray-200 rounded-lg w-1/2 mb-6"></div>
                                <div className="space-y-3">
                                  <div className="h-4 bg-gray-200 rounded-lg"></div>
                                  <div className="h-4 bg-gray-200 rounded-lg w-5/6"></div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex-1 overflow-y-auto">
                          {boards.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-8">
                              {boards.map((board) => (
                                <div
                                  key={board.id}
                                  className="bg-gray-50 rounded-2xl border border-gray-200/50 hover:border-purple-200/50 hover:shadow-xl transition-all duration-200 cursor-pointer active:scale-95"
                                  onClick={() => handleBoardClick(board)}
                                >
                                  <div className="p-8">
                                    <div className="flex items-center justify-between mb-4">
                                      <h3 className="text-xl font-bold text-gray-900">
                                        {board.board_title}
                                      </h3>
                                      <span className="text-sm text-gray-600 bg-white px-3 py-1.5 rounded-lg border border-gray-200/50 font-medium">
                                        {board.assigned_users?.length || 0} member{(board.assigned_users?.length || 0) !== 1 ? 's' : ''}
                                      </span>
                                    </div>
                                    
                                    <div className="flex items-center justify-between text-base text-gray-600">
                                      <span>
                                        Created {new Date(board.created_at).toLocaleDateString()}
                                      </span>
                                      <ArrowRight size={20} className="text-purple-500" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-16">
                              <div className="bg-gray-50 rounded-2xl p-12 max-w-lg mx-auto shadow-sm border border-gray-200/50">
                                <div className="mx-auto h-24 w-24 text-gray-400 mb-6">
                                  <ListChecks size={96} className="mx-auto" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">No task boards yet</h3>
                                <p className="text-lg text-gray-600 mb-8">
                                  Get started by creating your first task board to organize your team's work.
                                </p>
                                <button
                                  onClick={() => setIsModalOpen(true)}
                                  className="inline-flex items-center space-x-3 bg-purple-700 text-white px-8 py-4 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-lg text-lg font-semibold"
                                >
                                  <Plus size={22} />
                                  <span>Create Your First Board</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      
      {/* Add Board Modal */}
      {userType && selectedBusiness && (
        <AddBoardModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onBoardCreated={handleBoardCreated}
          businessId={selectedBusiness.id}
          userInfo={{
            userType,
            resourceId,
            resourceName: resourceName || undefined
          }}
        />
      )}

      {/* Board Settings Modal */}
      {selectedBusiness && (
        <BoardSettingsModal
          isOpen={isSettingsModalOpen}
          onClose={handleCloseBoardSettings}
          board={selectedBoard}
          businessId={selectedBusiness.id}
          onBoardUpdated={handleBoardCreated}
        />
      )}

      {/* Booking Info Modal */}
      <BookingInfoModal
        isOpen={bookingInfoModalOpen}
        onClose={() => {
          setBookingInfoModalOpen(false);
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
        onUpdate={() => {
          // Refresh the board data if needed
          if (openedBoard) {
            handleBoardClick(openedBoard);
          }
        }}
      />
    </div>
  );
} 